// Steel Plate Configuration

import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';

export const APP_NAME = 'iSteelPlate';
export const APP_VERSION = '1.1.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

export type APIPayload = {
  title: string;
  Lx: string;
  Ly: string;
  tpl: string;
  damping: string;
  addedWeight: string;
  orientation: number;
  support: number;
  Fdy: string;
  Fdu: string;
  E: string;
  density: string;
  Pa: string; // pressure history csv
  td: string; // time history csv
  d0: string; // initial displacement
  v0: string; // initial velocity
};

export type ChartKey = 'loadChart' | 'dispChart' | 'rotChart' | 'reactionChart' | 'ductChart' | 'hysteresisChart';

export const PRINT_CHART_ORDER: ChartKey[] = ['loadChart', 'dispChart', 'rotChart', 'reactionChart', 'ductChart', 'hysteresisChart'];

export const PRINT_CHART_ASPECT = {
  loadChart: 'wide',
  dispChart: 'wide',
  rotChart: 'wide',
  reactionChart: 'wide',
  ductChart: 'square',
  hysteresisChart: 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 28 } },
      margin: { l: 86, r: 28, t: 72, b: 108 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  },
  square: {
    width: 600,
    height: 600,
    scale: 2,
    layout: {
      font: { size: 15 },
      title: { font: { size: 28 } },
      margin: { l: 88, r: 28, t: 78, b: 114 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 18 }, x: 0.5, xanchor: 'center', y: -0.32 }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(18rem, 70vw, 25rem)';

export const LIVE_PLOT_SIZE_BY_KEY = {
  loadChart: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '4 / 3'
  },
  dispChart: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '4 / 3'
  },
  rotChart: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '4 / 3'
  },
  reactionChart: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '4 / 3'
  },
  ductChart: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '1 / 1'
  },
  hysteresisChart: {
    fit: 'width',
    width: '100%',
    maxWidth: '31rem',
    aspectRatio: '1 / 1'
  }
} satisfies Record<ChartKey, LivePlotSizeOptions>;

export interface APIResponse {
  summary: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
}

// Frontend state for this page after analysis. The API returns summary + plotData only;
// `charts` is generated later from mounted LivePlot components for print/save.
export type SteelPlateResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

export const SUPPORT_TYPES_STEEL_PLATE: DropdownOption[] = [
  { value: 1, label: 'Pinned All Around' },
  { value: 2, label: 'Fixed All Around' }
];
